import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, AlertCircle, MapPin, Plus, Save, UserCheck, Users, X as XIcon } from "lucide-react";
import { api } from "@/lib/api";
import { TramitadorPicker } from "@/components/TramitadorPicker";
import { UserSearchOrCreate } from "@/components/UserSearchOrCreate";

interface SepomexEntrada {
  cp: string;
  asentamiento: string;
  tipoAsenta: string | null;
  municipio: string;
  estado: string;
}

interface SectorOption {
  id: string;
  codigo: string;
  nombre: string;
}

interface Tramitador {
  id: string;
  razonSocial: string;
  rfc: string;
  estatus: string;
}

type ModoAlta = "TITULAR" | "TRAMITADOR" | "TITULAR_CON_TRAMITADOR";

interface AltaRequest {
  tipoPersona: "MORAL" | "FISICA";
  puedeActuarComoTitular: boolean;
  puedeActuarComoTramitador: boolean;
  razonSocial: string;
  nombreComercial: string;
  rfc: string;
  curp: string;
  giroEmpresa: string;
  correoPrincipal: string;
  telefono: string;
  paginaElectronica: string;
  domicilio: {
    calle: string;
    numeroExterior: string;
    numeroInterior: string;
    colonia: string;
    municipio: string;
    entidadFederativa: string;
    codigoPostal: string;
    pais: string;
  };
  consentimientoAvisoPrivacidad: boolean;
  tieneFiliales: boolean;
  sectoresAutorizados: string[];
  tramitadorId?: string | null;
  usuariosAsociar?: { usuarioId: string; tipoAutorizacion?: string; notificar?: boolean }[];
}

interface UsuarioDelTramitador {
  id: string;
  correo: string;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string | null;
  tipo: string;
  relacionConTramitador: string;
}

// Valores alineados con el CHECK de kernel.asociacion_legal.
// "" (vacío) deja que el backend herede según el rol del usuario:
// REPRESENTANTE → FIRMA; cualquier otro → GESTION.
const TIPOS_AUTORIZACION = [
  { value: "", label: "Heredar del rol del usuario (recomendado)" },
  { value: "FIRMA", label: "Firma" },
  { value: "GESTION", label: "Gestión" },
  { value: "SEGUIMIENTO", label: "Seguimiento" },
  { value: "CONSULTA", label: "Consulta" },
];

const initial: AltaRequest = {
  tipoPersona: "MORAL",
  puedeActuarComoTitular: true,
  puedeActuarComoTramitador: false,
  razonSocial: "",
  nombreComercial: "",
  rfc: "",
  curp: "",
  giroEmpresa: "",
  correoPrincipal: "",
  telefono: "",
  paginaElectronica: "",
  domicilio: {
    calle: "",
    numeroExterior: "",
    numeroInterior: "",
    colonia: "",
    municipio: "",
    entidadFederativa: "",
    codigoPostal: "",
    pais: "MEX",
  },
  consentimientoAvisoPrivacidad: false,
  tieneFiliales: false,
  sectoresAutorizados: [],
};

export function RegistroLegalAltaPage() {
  const navigate = useNavigate();
  const [modo, setModo] = useState<ModoAlta>("TITULAR");
  const [tramitador, setTramitador] = useState<Tramitador | null>(null);
  const [form, setForm] = useState<AltaRequest>(initial);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Sincroniza banderas y tramitadorId según el modo elegido.
  const cambiarModo = (nuevo: ModoAlta) => {
    setModo(nuevo);
    if (nuevo === "TITULAR") {
      setForm((f) => ({ ...f, puedeActuarComoTitular: true, puedeActuarComoTramitador: false, tramitadorId: null }));
      setTramitador(null);
    } else if (nuevo === "TRAMITADOR") {
      setForm((f) => ({ ...f, puedeActuarComoTitular: false, puedeActuarComoTramitador: true, tramitadorId: null }));
      setTramitador(null);
    } else {
      // TITULAR_CON_TRAMITADOR
      setForm((f) => ({ ...f, puedeActuarComoTitular: true, puedeActuarComoTramitador: false }));
    }
  };

  const qSectores = useQuery<SectorOption[]>({
    queryKey: ["sectores"],
    queryFn: async () => (await api.get<SectorOption[]>("/api/catalogos/sectores")).data,
    staleTime: 5 * 60_000,
  });

  const toggleSector = (id: string) => {
    setForm((f) => ({
      ...f,
      sectoresAutorizados: f.sectoresAutorizados.includes(id)
        ? f.sectoresAutorizados.filter((x) => x !== id)
        : [...f.sectoresAutorizados, id],
    }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (modo === "TITULAR_CON_TRAMITADOR" && !tramitador) {
      setError("Selecciona el tramitador externo asociado.");
      return;
    }
    if (form.tipoPersona === "FISICA" && !form.curp.trim()) {
      setError("Persona física requiere CURP.");
      return;
    }
    setError(null);
    setLoading(true);
    try {
      const esEtt = modo === "TITULAR_CON_TRAMITADOR" && !!tramitador;
      const body: AltaRequest = {
        ...form,
        nombreComercial: form.nombreComercial.trim() || "",
        curp: form.curp.trim(),
        giroEmpresa: form.giroEmpresa.trim() || "",
        paginaElectronica: form.paginaElectronica.trim() || "",
        tramitadorId: esEtt ? tramitador!.id : null,
        usuariosAsociar: esEtt ? form.usuariosAsociar ?? [] : undefined,
      };
      const { data } = await api.post<{ id: string }>("/api/registros-legales", body);
      navigate(`/registros-legales/${data.id}`);
    } catch (e: unknown) {
      const r = e as { response?: { data?: { detail?: string; title?: string } } };
      setError(r.response?.data?.detail || r.response?.data?.title || (e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <Link
          to="/tramites-legales"
          className="inline-flex items-center gap-1 text-sm text-fg-muted hover:text-primary-strong"
        >
          <ChevronLeft className="h-4 w-4" />
          Volver a Trámites Legales
        </Link>
      </div>
      <header>
        <h1 className="text-2xl font-semibold text-fg">Alta de Registro Legal</h1>
        <p className="text-sm text-fg-muted">
          Captura el expediente base. Al guardar queda en estado <strong>APROBADO</strong> (Calidad ya validó la
          documentación previamente).
        </p>
      </header>

      <form onSubmit={submit} className="space-y-4">
        <Section title="Tipo de registro">
          <Field label="¿Cuál es el escenario?" full>
            <div className="space-y-2">
              {(
                [
                  { id: "TITULAR", label: "Empresa Titular", hint: "Solicita servicios directamente." },
                  { id: "TRAMITADOR", label: "Tramitador Externo", hint: "Gestiona trámites para terceros." },
                  {
                    id: "TITULAR_CON_TRAMITADOR",
                    label: "Empresa Titular con Tramitador",
                    hint: "Empresa titular cuyos trámites los opera un tramitador externo ya registrado.",
                  },
                ] as { id: ModoAlta; label: string; hint: string }[]
              ).map((opt) => (
                <label
                  key={opt.id}
                  className={`flex cursor-pointer items-start gap-3 rounded border p-3 ${
                    modo === opt.id
                      ? "border-primary-strong bg-primary-tint"
                      : "border-edge hover:border-edge-strong"
                  }`}
                >
                  <input
                    type="radio"
                    name="modo"
                    checked={modo === opt.id}
                    onChange={() => cambiarModo(opt.id)}
                    className="mt-0.5"
                  />
                  <div>
                    <div className="text-sm font-medium text-fg">{opt.label}</div>
                    <div className="text-xs text-fg-muted">{opt.hint}</div>
                  </div>
                </label>
              ))}
            </div>
          </Field>
          {modo === "TITULAR_CON_TRAMITADOR" && (
            <Field label="Tramitador externo asociado *" full>
              <TramitadorPicker value={tramitador} onChange={setTramitador} />
              <p className="mt-1 text-xs text-fg-subtle">
                Solo aparecen registros que ya tienen la bandera Tramitador. Si el tramitador aún no existe,
                créalo primero en otro alta.
              </p>
            </Field>
          )}
          <Field label="Tipo de persona">
            <select
              value={form.tipoPersona}
              onChange={(e) => setForm({ ...form, tipoPersona: e.target.value as "MORAL" | "FISICA" })}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            >
              <option value="MORAL">Moral</option>
              <option value="FISICA">Física</option>
            </select>
          </Field>
        </Section>

        {modo === "TITULAR_CON_TRAMITADOR" && tramitador && (
          <UsuariosTramitadorSection
            tramitador={tramitador}
            seleccionados={form.usuariosAsociar ?? []}
            onChange={(usuarios) => setForm((f) => ({ ...f, usuariosAsociar: usuarios }))}
          />
        )}

        <Section title="Identidad">
          <Field label="Razón social *" full>
            <input
              type="text"
              required
              value={form.razonSocial}
              onChange={(e) => setForm({ ...form, razonSocial: e.target.value })}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </Field>
          <Field label="Nombre comercial">
            <input
              type="text"
              value={form.nombreComercial}
              onChange={(e) => setForm({ ...form, nombreComercial: e.target.value })}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </Field>
          <Field label="RFC *">
            <input
              type="text"
              required
              value={form.rfc}
              onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })}
              className="w-full rounded border px-2 py-1.5 font-mono text-sm uppercase"
            />
          </Field>
          {form.tipoPersona === "FISICA" && (
            <Field label="CURP * (requerido para persona física)">
              <input
                type="text"
                required
                value={form.curp}
                onChange={(e) => setForm({ ...form, curp: e.target.value.toUpperCase() })}
                className="w-full rounded border px-2 py-1.5 font-mono text-sm uppercase"
              />
            </Field>
          )}
          <Field label="Giro de la empresa">
            <input
              type="text"
              value={form.giroEmpresa}
              onChange={(e) => setForm({ ...form, giroEmpresa: e.target.value })}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </Field>
        </Section>

        <Section title="Contacto">
          <Field label="Correo principal *">
            <input
              type="email"
              required
              value={form.correoPrincipal}
              onChange={(e) => setForm({ ...form, correoPrincipal: e.target.value })}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </Field>
          <Field label="Teléfono *">
            <input
              type="text"
              required
              value={form.telefono}
              onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </Field>
          <Field label="Página electrónica">
            <input
              type="text"
              value={form.paginaElectronica}
              onChange={(e) => setForm({ ...form, paginaElectronica: e.target.value })}
              className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
            />
          </Field>
        </Section>

        <DomicilioSection form={form} setForm={setForm} />


        <Section title="Sectores autorizados">
          <Field label="Selecciona uno o más" full>
            <div className="flex flex-wrap gap-2">
              {(qSectores.data ?? []).map((s) => {
                const active = form.sectoresAutorizados.includes(s.id);
                return (
                  <button
                    type="button"
                    key={s.id}
                    onClick={() => toggleSector(s.id)}
                    className={`rounded border px-3 py-1.5 text-sm ${
                      active
                        ? "border-primary bg-primary text-white"
                        : "border-edge-strong bg-surface text-fg-muted hover:bg-surface-2"
                    }`}
                  >
                    {s.nombre}
                  </button>
                );
              })}
            </div>
          </Field>
        </Section>

        <Section title="Otros">
          <Field label="Consentimiento aviso de privacidad" full>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.consentimientoAvisoPrivacidad}
                onChange={(e) => setForm({ ...form, consentimientoAvisoPrivacidad: e.target.checked })}
              />
              <span>El titular ha consentido el aviso de privacidad.</span>
            </label>
          </Field>
          <Field label="¿Tiene filiales?" full>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.tieneFiliales}
                onChange={(e) => setForm({ ...form, tieneFiliales: e.target.checked })}
              />
              <span>Esta empresa tiene filiales o subsidiarias.</span>
            </label>
          </Field>
        </Section>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border bg-estado-warn-bg p-3 text-sm text-estado-warn">
            <AlertCircle className="mt-0.5 h-4 w-4" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t bg-surface-2 px-4 py-3">
          <button
            type="button"
            onClick={() => navigate("/tramites-legales")}
            className="rounded border px-3 py-1.5 text-sm text-fg-muted hover:bg-surface-2"
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="inline-flex items-center gap-1 rounded bg-primary px-3 py-1.5 text-sm text-white hover:bg-primary-hover disabled:opacity-50"
            disabled={loading}
          >
            <Save className="h-4 w-4" />
            {loading ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </form>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border bg-surface shadow-card">
      <h2 className="border-b px-4 py-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">{title}</h2>
      <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
    </section>
  );
}

function Field({ label, children, full }: { label: string; children: React.ReactNode; full?: boolean }) {
  return (
    <div className={full ? "sm:col-span-2 lg:col-span-3" : ""}>
      <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-fg-subtle">{label}</div>
      {children}
    </div>
  );
}

/**
 * Sección de domicilio fiscal con cascada SEPOMEX: al teclear un CP de 5
 * dígitos se autocompletan estado/municipio y se ofrece dropdown de colonias
 * del CP. Si el CP no tiene match en SEPOMEX, los campos quedan editables
 * manualmente.
 */
function DomicilioSection({
  form,
  setForm,
}: {
  form: AltaRequest;
  setForm: React.Dispatch<React.SetStateAction<AltaRequest>>;
}) {
  const cp = form.domicilio.codigoPostal;
  const qSepomex = useQuery<SepomexEntrada[]>({
    queryKey: ["sepomex", cp],
    enabled: /^[0-9]{5}$/.test(cp),
    queryFn: async () => (await api.get<SepomexEntrada[]>(`/api/sepomex`, { params: { cp } })).data,
  });

  useEffect(() => {
    const entradas = qSepomex.data;
    if (!entradas || entradas.length === 0) return;
    setForm((f) => {
      if (f.domicilio.entidadFederativa !== "" && entradas.some((e) => e.asentamiento === f.domicilio.colonia)) {
        return f;
      }
      const primera = entradas[0];
      return {
        ...f,
        domicilio: {
          ...f.domicilio,
          entidadFederativa: f.domicilio.entidadFederativa || primera.estado,
          municipio: f.domicilio.municipio || primera.municipio,
          colonia: entradas.some((e) => e.asentamiento === f.domicilio.colonia)
            ? f.domicilio.colonia
            : primera.asentamiento,
        },
      };
    });
  }, [qSepomex.data, setForm]);

  const colonias = qSepomex.data ?? [];
  const sinResultadosCP = /^[0-9]{5}$/.test(cp) && qSepomex.isFetched && colonias.length === 0;

  const setDom = (patch: Partial<AltaRequest["domicilio"]>) =>
    setForm((f) => ({ ...f, domicilio: { ...f.domicilio, ...patch } }));

  return (
    <Section title="Domicilio fiscal">
      <Field label="Código postal *">
        <input
          type="text"
          required
          inputMode="numeric"
          pattern="^[0-9]{5}$"
          maxLength={5}
          value={cp}
          onChange={(e) => {
            const val = e.target.value.replace(/[^0-9]/g, "");
            const limpiar = val !== cp && val.length < 5;
            setDom({
              codigoPostal: val,
              ...(limpiar ? { entidadFederativa: "", municipio: "", colonia: "" } : {}),
            });
          }}
          className="w-full rounded border px-2 py-1.5 font-mono text-sm"
        />
        {qSepomex.isFetching && (
          <p className="mt-1 flex items-center gap-1 text-xs text-fg-subtle">
            <MapPin className="h-3 w-3" /> Buscando colonia…
          </p>
        )}
        {sinResultadosCP && (
          <p className="mt-1 text-xs text-estado-warn">
            No hay coincidencia en SEPOMEX para ese CP. Captura los campos manualmente.
          </p>
        )}
      </Field>
      <Field label="Entidad federativa *">
        <input
          type="text"
          required
          value={form.domicilio.entidadFederativa}
          onChange={(e) => setDom({ entidadFederativa: e.target.value })}
          disabled={colonias.length > 0}
          className="w-full rounded border px-2 py-1.5 text-sm disabled:bg-surface-2"
        />
      </Field>
      <Field label="Municipio o demarcación *">
        <input
          type="text"
          required
          value={form.domicilio.municipio}
          onChange={(e) => setDom({ municipio: e.target.value })}
          disabled={colonias.length > 0}
          className="w-full rounded border px-2 py-1.5 text-sm disabled:bg-surface-2"
        />
      </Field>
      <Field label="Colonia *" full>
        {colonias.length > 0 ? (
          <select
            required
            value={form.domicilio.colonia}
            onChange={(e) => setDom({ colonia: e.target.value })}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          >
            <option value="">— Selecciona colonia —</option>
            {colonias.map((c, i) => (
              <option key={`${c.asentamiento}-${i}`} value={c.asentamiento}>
                {c.asentamiento}
                {c.tipoAsenta ? ` (${c.tipoAsenta})` : ""}
              </option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            required
            value={form.domicilio.colonia}
            onChange={(e) => setDom({ colonia: e.target.value })}
            className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
          />
        )}
      </Field>
      <Field label="Calle *" full>
        <input
          type="text"
          required
          value={form.domicilio.calle}
          onChange={(e) => setDom({ calle: e.target.value })}
          className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
        />
      </Field>
      <Field label="Número exterior *">
        <input
          type="text"
          required
          value={form.domicilio.numeroExterior}
          onChange={(e) => setDom({ numeroExterior: e.target.value })}
          className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
        />
      </Field>
      <Field label="Número interior">
        <input
          type="text"
          value={form.domicilio.numeroInterior}
          onChange={(e) => setDom({ numeroInterior: e.target.value })}
          className="w-full rounded border border-edge-strong bg-surface px-2 py-1.5 text-sm text-fg"
        />
      </Field>
    </Section>
  );
}

/**
 * Sección visible sólo en variante ETT: lista usuarios ya vinculados al
 * tramitador (representantes activos + gestores en cartas vigentes) con
 * checkbox para asociarlos al nuevo titular. Permite además agregar
 * usuarios adicionales del sistema (o crearlos) vía UserSearchOrCreate.
 *
 * Backend: cada usuario seleccionado se envía en `usuariosAsociar` del
 * alta; el servicio crea una asociación legal bajo la carta auto-creada
 * con los sectores del titular y el tipo de autorización indicado.
 */
function UsuariosTramitadorSection({
  tramitador,
  seleccionados,
  onChange,
}: {
  tramitador: Tramitador;
  seleccionados: { usuarioId: string; tipoAutorizacion?: string; notificar?: boolean }[];
  onChange: (usuarios: { usuarioId: string; tipoAutorizacion?: string; notificar?: boolean }[]) => void;
}) {
  const [externosAgregados, setExternosAgregados] = useState<UsuarioDelTramitador[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  const q = useQuery<UsuarioDelTramitador[]>({
    queryKey: ["usuarios-del-tramitador", tramitador.id],
    queryFn: async () =>
      (await api.get<UsuarioDelTramitador[]>(
        `/api/registros-legales/${tramitador.id}/usuarios-asociados`,
      )).data,
  });

  // Combina usuarios del tramitador (BD) + externos agregados manualmente.
  const todos: UsuarioDelTramitador[] = [
    ...(q.data ?? []),
    ...externosAgregados.filter((e) => !(q.data ?? []).some((u) => u.id === e.id)),
  ];

  const estaSeleccionado = (id: string) => seleccionados.some((s) => s.usuarioId === id);
  const tipoDe = (id: string) =>
    seleccionados.find((s) => s.usuarioId === id)?.tipoAutorizacion ?? "";
  const notificarDe = (id: string) => {
    const sel = seleccionados.find((s) => s.usuarioId === id);
    return sel?.notificar ?? true;
  };

  const toggle = (id: string) => {
    if (estaSeleccionado(id)) {
      onChange(seleccionados.filter((s) => s.usuarioId !== id));
    } else {
      // Sin tipoAutorizacion → backend lo hereda (REPRESENTANTE→FIRMA, GESTOR→GESTION).
      onChange([...seleccionados, { usuarioId: id }]);
    }
  };

  const cambiarTipo = (id: string, tipo: string) => {
    onChange(seleccionados.map((s) => {
      if (s.usuarioId !== id) return s;
      const next: { usuarioId: string; tipoAutorizacion?: string; notificar?: boolean } = { usuarioId: s.usuarioId };
      if (tipo) next.tipoAutorizacion = tipo;
      if (s.notificar === false) next.notificar = false;
      return next;
    }));
  };

  const cambiarNotificar = (id: string, notificar: boolean) => {
    onChange(seleccionados.map((s) => {
      if (s.usuarioId !== id) return s;
      const next: { usuarioId: string; tipoAutorizacion?: string; notificar?: boolean } = { usuarioId: s.usuarioId };
      if (s.tipoAutorizacion) next.tipoAutorizacion = s.tipoAutorizacion;
      if (!notificar) next.notificar = false;
      return next;
    }));
  };

  const asociarExterno = async (usuarioId: string, notificar: boolean) => {
    try {
      const { data } = await api.get<UsuarioDelTramitador>(`/api/iam/usuarios/${usuarioId}`);
      setExternosAgregados((prev) =>
        prev.some((p) => p.id === usuarioId) ? prev : [
          ...prev,
          {
            ...data,
            relacionConTramitador: data.relacionConTramitador ?? "externo agregado",
          },
        ],
      );
    } catch {
      // Si el GET de IAM falla, al menos guardamos el id con datos mínimos.
      setExternosAgregados((prev) =>
        prev.some((p) => p.id === usuarioId) ? prev : [
          ...prev,
          { id: usuarioId, correo: "(externo)", nombre: "", apellidoPaterno: "", apellidoMaterno: null, tipo: "", relacionConTramitador: "externo agregado" },
        ],
      );
    }
    const nuevo: { usuarioId: string; notificar?: boolean } = { usuarioId };
    if (!notificar) nuevo.notificar = false;
    onChange([...seleccionados, nuevo]);
    setModalOpen(false);
  };

  return (
    <section className="rounded-lg border bg-surface shadow-card">
      <div className="flex items-center justify-between border-b px-4 py-2">
        <div>
          <h2 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-fg-subtle">
            <Users className="h-4 w-4" /> Usuarios autorizados del tramitador
          </h2>
          <p className="mt-0.5 text-xs text-fg-subtle">
            Selecciona quiénes de {tramitador.razonSocial} podrán también operar este nuevo titular.
            Los sectores se heredan automáticamente del titular.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setModalOpen(true)}
          className="inline-flex items-center gap-1 rounded border border-primary-strong px-3 py-1 text-xs font-medium text-primary-strong hover:bg-primary hover:text-white"
        >
          <Plus className="h-3 w-3" /> Agregar otro
        </button>
      </div>
      <div className="p-4">
        {q.isLoading ? (
          <div className="text-sm text-fg-subtle">Cargando usuarios del tramitador…</div>
        ) : todos.length === 0 ? (
          <div className="rounded border border-dashed border-edge-strong p-3 text-sm text-fg-subtle">
            Este tramitador aún no tiene usuarios vinculados. Usa "Agregar otro" para buscarlos en el sistema
            o crearlos.
          </div>
        ) : (
          <ul className="space-y-1.5">
            {todos.map((u) => {
              const sel = estaSeleccionado(u.id);
              return (
                <li
                  key={u.id}
                  className={`flex flex-wrap items-center gap-3 rounded border p-2 text-sm ${
                    sel ? "border-primary-strong bg-primary-tint" : "border-edge"
                  }`}
                >
                  <label className="flex flex-1 items-start gap-2">
                    <input
                      type="checkbox"
                      checked={sel}
                      onChange={() => toggle(u.id)}
                      className="mt-1"
                    />
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium text-fg">
                          {u.nombre} {u.apellidoPaterno} {u.apellidoMaterno ?? ""}
                        </span>
                        <span className="text-xs text-fg-subtle">{u.correo}</span>
                      </div>
                      <div className="text-[11px] text-fg-subtle">
                        {u.tipo} · {u.relacionConTramitador}
                      </div>
                    </div>
                  </label>
                  {sel && (
                    <>
                      <label className="flex items-center gap-1 text-xs text-fg-muted">
                        <UserCheck className="h-3 w-3" />
                        <select
                          value={tipoDe(u.id)}
                          onChange={(e) => cambiarTipo(u.id, e.target.value)}
                          className="rounded border px-1.5 py-0.5 text-xs"
                        >
                          {TIPOS_AUTORIZACION.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </label>
                      <label className="flex items-center gap-1 text-xs text-fg-muted" title="Si está marcado, el usuario recibirá correo de asociación al guardar el titular.">
                        <input
                          type="checkbox"
                          checked={notificarDe(u.id)}
                          onChange={(e) => cambiarNotificar(u.id, e.target.checked)}
                        />
                        Notificar
                      </label>
                    </>
                  )}
                  {externosAgregados.some((e) => e.id === u.id) && (
                    <button
                      type="button"
                      onClick={() => {
                        setExternosAgregados((prev) => prev.filter((p) => p.id !== u.id));
                        onChange(seleccionados.filter((s) => s.usuarioId !== u.id));
                      }}
                      className="rounded p-0.5 text-estado-err hover:bg-estado-err-bg"
                      aria-label="Quitar externo"
                    >
                      <XIcon className="h-3 w-3" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <UserSearchOrCreate
        open={modalOpen}
        title="Agregar usuario para asociar al titular"
        onClose={() => setModalOpen(false)}
        onAsociar={asociarExterno}
      />
    </section>
  );
}
